import { core, identity } from './client';
import type {
  AdministratorInvitation,
  AttentionItem,
  AuditEvent,
  AuditSearchFilters,
  AuditView,
  Branch,
  BulkApplyGroupProductRequest,
  BulkProductAction,
  BulkProductAppliedEntry,
  BulkProductApplyResult,
  BulkProductSkippedEntry,
  Campus,
  ChangeMasterDataStatusRequest,
  CreateBranchRequest,
  CreateCampusRequest,
  CreateGroupRequest,
  CreateInstitutionMappingRequest,
  CreateMappingRequest,
  CreateMasterDataValueRequest,
  CreateOnboardingCaseRequest,
  Delegation,
  DomainVerificationInstruction,
  FeatureDefinitionSummary,
  FeatureFlagScope,
  FeatureFlagSetting,
  FeatureFlagVersion,
  GrantDelegationRequest,
  GrantPolicyOverrideRequest,
  GroupAnalytics,
  GroupAnalyticsInstitution,
  GroupCurrencyBreakdownEntry,
  GroupFinancial,
  GroupMembershipRole,
  GroupMembershipSummary,
  GroupProductSubscriptionEntry,
  ImportBatch,
  ImportBatchState,
  ImportProgress,
  IncidentSeverity,
  IncidentStatus,
  InheritedPermissionReview,
  InstitutionBranding,
  InstitutionGroup,
  InstitutionGroupMember,
  InstitutionGroupType,
  InstitutionIntegrationMapping,
  InstitutionLifecycle,
  InstitutionLifecycleEvent,
  InstitutionPolicyOverride,
  InstitutionProductAssignment,
  ProductCatalogueEntry,
  BootstrapInstitutionRequest,
  ConnectionSecretKind,
  ConnectionSecretStatus,
  CredentialTestResult,
  PutConnectionSecretRequest,
  InstitutionStructure,
  InstitutionType,
  InstitutionTypeCount,
  IntegrationCapability,
  IntegrationEnvironment,
  IntegrationHealth,
  IntegrationHealthCheckResult,
  IntegrationMapping,
  IntegrationMappingHistoryEntry,
  IntegrationProbeResult,
  IntegrationRegistration,
  IntegrationState,
  IntegrationSystemCode,
  InvitationFailure,
  InvitationStatus,
  InvitationType,
  InviteAdministratorRequest,
  LifecycleAction,
  LifecycleActionRequest,
  LifecycleActionResult,
  LifecycleImpact,
  MaintenanceMode,
  MappedEntityType,
  MasterDataDomain,
  MasterDataValue,
  OnboardingCaseDetail,
  OnboardingCaseSummary,
  OnboardingChecklistItem,
  OnboardingHistoryEntry,
  OnboardingOutstandingAction,
  OnboardingReadiness,
  OnboardingState,
  OnboardingTask,
  OperationalSignal,
  Page,
  PageRequest,
  PlatformAlert,
  PlatformAuditRecord,
  ConnectionHealthView,
  PlatformConnection,
  PlatformConnectionProbeResult,
  PlatformDashboard,
  PlatformHealthSnapshot,
  PlatformIncident,
  PlatformInstitutionRecord,
  PlatformPolicyVersion,
  PlatformReport,
  PlatformReportCode,
  PolicyMinimums,
  PrivilegedChange,
  ProductActivationSummary,
  ProductMaintenanceWindow,
  PublishMasterDataVersionRequest,
  QuarantineReport,
  QuarantineReportEntry,
  RegisterIntegrationRequest,
  RegisterPlatformConnectionRequest,
  ReportParameterValues,
  RestoreBrandingDefaultsRequest,
  ReportRow,
  RotateCredentialReferenceRequest,
  RequestSupportAccessRequest,
  ResolvedFeatureFlag,
  ResolvedPolicy,
  ResolvedPolicySet,
  ServiceHealth,
  SetFeatureFlagRequest,
  SetIntegrationStateRequest,
  SubscribeProductRequest,
  SupportAccessGrant,
  SupportSessionDescriptor,
  TransitionOnboardingCaseRequest,
  TransitionProductStateRequest,
  UpdateBrandingRequest,
  UpdateGroupRequest,
  UpdateInstitutionMappingRequest,
  UpdateIntegrationRequest,
  UpdatePlatformConnectionRequest,
} from '@/types/platform';
import { BULK_PRODUCT_ACTIONS, INTEGRATION_CAPABILITIES, PLATFORM_REPORT_ROUTES } from '@/types/platform';

/**
 * Every platform-administration call, in one place.
 *
 * ROUTING. Core owns the platform-admin module (its PLATFORM_ADMIN_ROUTE_PREFIX
 * is 'platform', giving /api/v1/platform/...), so institutions, onboarding cases,
 * product activation, lifecycle, policies, operations, imports, master data and
 * the operational reports go to `core`. People, invitations and sign-in accounts
 * have always been the identity service's, so administrator invitations,
 * delegations, support access and the security audit go to `identity`. The BFF
 * strips its own /api/v1/bff/<upstream> prefix and re-adds the upstream's base, so
 * the paths below are exactly what the services mount.
 *
 * WHAT CHANGED, AND WHY IT MATTERED. This layer was written in parallel with the
 * services and drifted from them. Several screens reported "not available yet"
 * for features that had shipped, because the console was calling a path nobody
 * had ever served: three product verbs instead of one transition, /imports
 * instead of /imports/batches, a policy model that was never built. Worse than
 * any of the 404s was the drift that did NOT fail — the onboarding list required
 * a field called `institutionName` that core has never sent, so every case was
 * filtered out and the pipeline read as empty. A silent wrong answer beats a loud
 * missing one every time, which is why the normalisers below are pinned to the
 * entity field names rather than to a hopeful contract.
 *
 * DEGRADATION. Two endpoints genuinely do not exist: the institution list and the
 * institution detail. Those calls are left exactly as they are so the screens
 * behind them keep saying so, and nothing here substitutes invented data for
 * them. What this layer does do is refuse to pass a malformed body through to
 * React — a response whose `needsAttention` is a string instead of an array would
 * otherwise reach `.map()` and blank the app, which is how this codebase went
 * down once before.
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Keeps only array members that carry the fields the UI dereferences.
 *
 * The key lists are typed against T, so renaming a field in the contract breaks
 * this call rather than silently filtering every row away at runtime.
 *
 * `nullable` exists because `null` is a real value in several of these contracts
 * and is not the same as absence. `InstitutionTypeCount.institutionType` is the
 * bucket the taxonomy change left institutions in whose old type has no honest
 * counterpart; treating it as a missing field dropped that bucket from the
 * dashboard entirely, so the portfolio chart quietly stopped adding up to the
 * total printed next to it.
 */
function rowsWith<T extends object>(
  value: unknown,
  required: readonly (keyof T & string)[],
  nullable: readonly (keyof T & string)[] = [],
): T[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is T => {
    if (!isRecord(item)) return false;
    if (!required.every((key) => item[key] !== undefined && item[key] !== null)) return false;
    return nullable.every((key) => item[key] !== undefined);
  });
}

/** Unwraps the common envelopes so a bare array and `{ items: [...] }` both work. */
function unwrap(payload: unknown, ...keys: string[]): unknown {
  if (Array.isArray(payload)) return payload;
  if (!isRecord(payload)) return undefined;
  for (const key of [...keys, 'items', 'data', 'results']) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  return undefined;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}

/**
 * The generated institution code, whichever shape it arrives in.
 *
 * Tolerant on the way in and strict on the way out. The create form keeps its
 * submit button disabled until a code has been generated, so a body this cannot
 * read must throw rather than resolve to an empty string — an empty string would
 * re-enable the button and produce a case with no code, which collides on the
 * unique index and is far harder to explain than a preview that failed.
 */
function normalizeCodePreview(payload: unknown): string {
  let current: unknown = payload;
  for (let depth = 0; depth < 4; depth += 1) {
    if (typeof current === 'string' && current.trim().length > 0) return current.trim();
    if (!isRecord(current)) break;

    const code = current.code ?? current.institutionCode;
    if (typeof code === 'string' && code.trim().length > 0) return code.trim();

    const nested = current.data ?? current.result ?? current.preview;
    if (nested === undefined || nested === current) break;
    current = nested;
  }
  throw new Error('The server returned an invalid institution code preview.');
}

function toCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * A count that is optional in the contract, so absence stays absence.
 *
 * Used for the per-member financial figures, where an unreported figure must
 * NOT become a confident zero on screen. Coercing it here would smuggle the lie
 * past the component that renders the em dash.
 */
function optionalCount(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * A percentage that may be genuinely null.
 *
 * `collectionRate` has three states: a number (0–100), null (nothing billed, so
 * no rate exists), or absent (this member's tenant has not reported). All three
 * must survive normalization, because the finance tab renders them differently.
 */
function optionalNullableCount(value: unknown): number | null | undefined {
  if (value === null) return null;
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/* ─── PA-01 ─── */

function normalizeDashboard(payload: unknown): PlatformDashboard {
  // An unusable body is not an empty dashboard — every panel is left absent so
  // each one renders "unavailable" instead of a confident zero.
  if (!isRecord(payload)) return {};
  const root = isRecord(payload.dashboard) ? payload.dashboard : payload;

  const counts = isRecord(root.counts) ? root.counts : undefined;

  return {
    counts: counts
      ? {
          total: toCount(counts.total),
          active: toCount(counts.active),
          awaitingOnboarding: toCount(counts.awaitingOnboarding),
          suspended: toCount(counts.suspended),
        }
      : undefined,
    // `institutionType` is nullable and null is its own bucket — see rowsWith.
    byType:
      root.byType === undefined
        ? undefined
        : rowsWith<InstitutionTypeCount>(root.byType, ['count'], ['institutionType']),
    needsAttention:
      root.needsAttention === undefined
        ? undefined
        : rowsWith<AttentionItem>(root.needsAttention, ['institutionId', 'institutionName', 'reason']),
    integrations:
      root.integrations === undefined
        ? undefined
        : rowsWith<IntegrationHealth>(root.integrations, ['id', 'name', 'state']),
    productActivations:
      root.productActivations === undefined
        ? undefined
        : rowsWith<ProductActivationSummary>(root.productActivations, ['institutionId', 'productCode', 'state']),
    invitationFailures:
      root.invitationFailures === undefined
        ? undefined
        : rowsWith<InvitationFailure>(root.invitationFailures, ['invitationId', 'email', 'status']),
    services: root.services === undefined ? undefined : rowsWith<ServiceHealth>(root.services, ['name', 'state']),
    privilegedChanges:
      root.privilegedChanges === undefined
        ? undefined
        : rowsWith<PrivilegedChange>(root.privilegedChanges, ['id', 'action', 'actor', 'occurredAt']),
    onboardingTasks:
      root.onboardingTasks === undefined
        ? undefined
        : rowsWith<OnboardingTask>(root.onboardingTasks, ['caseId', 'label', 'state']),
    alerts: root.alerts === undefined ? undefined : rowsWith<PlatformAlert>(root.alerts, ['id', 'title', 'severity']),
    generatedAt: optionalString(root.generatedAt),
  };
}

/* ─── PA-02 ─── */

/**
 * The fields OnboardingCase actually has.
 *
 * `legalName` and `caseReference`, not `institutionName` and `reference`. The old
 * key list required a field core has never sent, so `rowsWith` discarded every
 * case and the screen said the pipeline was empty. Nothing failed; the work simply
 * became invisible.
 */
const CASE_KEYS = ['id', 'caseReference', 'legalName', 'state'] as const;

function normalizeCaseList(payload: unknown): OnboardingCaseSummary[] {
  return rowsWith<OnboardingCaseSummary>(unwrap(payload, 'cases'), CASE_KEYS, ['institutionType']);
}

function normalizeCaseDetail(payload: unknown): OnboardingCaseDetail {
  let root: unknown = payload;
  // Core responses are wrapped by the standard response envelope. Accept the
  // envelope as well as the older `{ case: ... }` projection so a successful
  // create/get response is not mistaken for malformed data.
  for (let depth = 0; depth < 4; depth += 1) {
    if (!isRecord(root)) break;
    if (typeof root.id === 'string' && typeof root.state === 'string') break;
    const nested = root.case ?? root.data ?? root.result;
    if (nested === undefined || nested === root) break;
    root = nested;
  }
  if (!isRecord(root) || typeof root.id !== 'string' || typeof root.state !== 'string') {
    throw new Error('The server returned an onboarding case this app cannot read.');
  }
  const detail = root as unknown as OnboardingCaseDetail;
  return {
    ...detail,
    checklist:
      root.checklist === undefined
        ? undefined
        : rowsWith<OnboardingChecklistItem>(root.checklist, ['id', 'code', 'label']),
    history:
      root.history === undefined
        ? undefined
        : rowsWith<OnboardingHistoryEntry>(root.history, ['id', 'toState', 'occurredAt']),
    actions:
      root.actions === undefined
        ? undefined
        : rowsWith<OnboardingOutstandingAction>(root.actions, ['id', 'title', 'status']),
  };
}

/* ─── Institutions ─── */

/**
 * Core's platform_institutions row.
 *
 * `legalName` and `code`, not `name`. There is no list or detail endpoint yet —
 * see `institutions.list` — but the structure endpoint returns one of these, so
 * the shape is pinned to the table rather than to a contract nobody has written.
 */
function normalizeInstitutionList(payload: unknown): PlatformInstitutionRecord[] {
  // `name`, not `legalName`. The directory sends the resolved name and no legal name
  // at all, so requiring `legalName` here made rowsWith discard every row and the
  // register read empty with nothing logged. `institutionType` is nullable-but-present:
  // null is a real answer (unclassified) and must not cause the row to be dropped.
  return rowsWith<PlatformInstitutionRecord>(
    unwrap(payload, 'institutions'),
    ['id', 'code', 'name'],
    ['institutionType'],
  );
}

function normalizeInstitutionDetail(payload: unknown): PlatformInstitutionRecord {
  const root = isRecord(payload) && isRecord(payload.institution) ? payload.institution : payload;
  // `name`, for the same reason as the list above. Requiring `legalName` threw on a
  // well-formed response, and because a plain Error carries no HTTP status the screen
  // rendered it as "could not reach the service" — sending the reader to check the
  // network for what was a parse fault against a response that had arrived intact.
  if (!isRecord(root) || typeof root.id !== 'string' || typeof root.name !== 'string') {
    throw new Error('The server returned an institution this app cannot read.');
  }
  return root as unknown as PlatformInstitutionRecord;
}

function normalizeProducts(payload: unknown): InstitutionProductAssignment[] {
  return rowsWith<InstitutionProductAssignment>(unwrap(payload, 'products'), ['productCode', 'state']);
}

/**
 * The platform product catalogue.
 *
 * Sorted here rather than relying on the server: `displayOrder` is what the catalogue means
 * by "the order these should be offered in", and a picker that lists products in whatever
 * order a query returned them reshuffles between visits. Rows missing a code or name are
 * dropped rather than rendered as a blank option nobody can identify.
 */
function normalizeCatalogue(payload: unknown): ProductCatalogueEntry[] {
  const rows = rowsWith<ProductCatalogueEntry>(unwrap(payload, 'products', 'data'), ['code', 'name']);
  return [...rows].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || a.name.localeCompare(b.name),
  );
}

/* ─── PA-07 ─── */

/**
 * The invitation whatever write path answered with.
 *
 * Invite returns the full result record (which carries `invitationId`, not
 * `id`), while resend and revoke return the summary shape (`id`). Both are
 * reduced to the frontend's AdministratorInvitation; an invitation whose id
 * or email cannot be read is refused rather than half-rendered.
 */
function normalizeInvitation(payload: unknown): AdministratorInvitation {
  const root = isRecord(payload) && isRecord(payload.invitation) ? payload.invitation : payload;
  if (!isRecord(root) || typeof root.email !== 'string') {
    throw new Error('The server returned an invitation this app cannot read.');
  }
  const id = optionalString(root.invitationId) ?? optionalString(root.id);
  if (!id) {
    throw new Error('The server returned an invitation this app cannot read.');
  }
  return {
    id,
    email: root.email,
    invitationType: (optionalString(root.invitationType) ?? 'PRIMARY_ADMINISTRATOR') as InvitationType,
    initialRoleNames: stringList(root.initialRoleNames),
    status: (optionalString(root.status) ?? 'PENDING') as InvitationStatus,
    expiresAt: optionalString(root.expiresAt),
    acceptedAt: optionalString(root.acceptedAt),
    resendCount: toCount(root.resendCount),
    lastSentAt: optionalString(root.lastSentAt),
    // Group-scoped invitations carry groupName/groupType on the list shape and
    // omit them (or drop them) elsewhere; either way the panel renders only
    // what the server actually said.
    groupId: optionalString(root.groupId) ?? optionalString((root.group as Record<string, unknown> | undefined)?.id),
    groupName:
      optionalString(root.groupName) ?? optionalString((root.group as Record<string, unknown> | undefined)?.name),
    groupType:
      optionalString(root.groupType) ?? optionalString((root.group as Record<string, unknown> | undefined)?.type),
  };
}

/**
 * GET /platform/structure/groups/:groupId/analytics.
 *
 * The projection is generated by core and its institution rows join member
 * state (status, currency) with the product catalogue. The required fields are
 * the ones every consumer renders; a row that cannot answer those is dropped
 * the way every other list is — the alternative is a table cell that silently
 * means nothing. `campuses`/`branches` are optional because core's finance
 * projection is still settling them.
 */
export function normalizeGroupAnalytics(payload: unknown): GroupAnalytics {
  const root = isRecord(payload) ? payload : {};
  const groupId = optionalString(root.groupId);
  const groupName = optionalString(root.groupName);
  if (!groupId || !groupName) {
    throw new Error('The server returned group analytics this app cannot read.');
  }
  return {
    groupId,
    groupCode: optionalString(root.groupCode) ?? '',
    groupName,
    groupType: (optionalString(root.groupType) ?? 'SCHOOL_GROUP') as InstitutionGroupType,
    institutionCount: toCount(root.institutionCount),
    activeInstitutionCount: toCount(root.activeInstitutionCount),
    campusCount: toCount(root.campusCount),
    branchCount: toCount(root.branchCount),
    currencyBreakdown: rowsWith<GroupCurrencyBreakdownEntry>(root.currencyBreakdown, ['currency', 'institutionCount']),
    productSubscriptions: rowsWith<GroupProductSubscriptionEntry>(root.productSubscriptions, [
      'productCode',
      'institutionCount',
    ]),
    institutions: rowsWith<GroupAnalyticsInstitution>(root.institutions, ['institutionId', 'code', 'name']),
    financial: normalizeGroupFinancial(root),
    generatedAt: optionalString(root.generatedAt),
  };
}

/**
 * An academic sub-object on a member row: admissions, capacity or progression.
 *
 * The three states of every figure must survive — undefined (core did not send
 * the slot), null (EduCore reported no value, e.g. a conversion rate with no
 * applications), and a number. Each inner field goes through
 * `optionalNullableCount` so the finance tab can render all three differently.
 */
function nullableAcademicObject(
  source: unknown,
  fields: readonly string[],
): Record<string, number | null | undefined> | null | undefined {
  if (source === null) return null;
  if (!isRecord(source)) return undefined;
  const result: Record<string, number | null | undefined> = {};
  for (const key of fields) {
    result[key] = optionalNullableCount(source[key]);
  }
  return result;
}

/**
 * One group-total academic sub-object: admissions, capacity or progression.
 *
 * The totals are all-or-nothing by contract: a count is only present when
 * EVERY available institution reported that exact count, and a partial total
 * would under-count. This therefore returns undefined unless every `counts`
 * key is a real number; `rates` (0–100, null when there is nothing to
 * compute) are included only when core sent them.
 */
function groupTotalsSubObject(
  source: Record<string, unknown>,
  counts: readonly string[],
  rates: readonly string[],
): Record<string, number | null> | undefined {
  if (!counts.every((key) => optionalCount(source[key]) !== undefined)) return undefined;
  const result: Record<string, number | null> = {};
  for (const key of counts) {
    result[key] = optionalCount(source[key]) as number;
  }
  for (const key of rates) {
    const rate = optionalNullableCount(source[key]);
    if (rate !== undefined) result[key] = rate;
  }
  return result;
}

/**
 * The optional `financial` section of the analytics projection.
 *
 * ADDITIVE AND ABSENT BY DESIGN — see GroupFinancial. A deployment that has not
 * caught up with the analytics phase returns no `financial` key, and this
 * returns undefined so the finance tab can say "not available yet" instead of
 * inventing zeros. `available: false` is core saying aggregation has not been
 * produced; `totals: null` is the aggregate genuinely missing while the member
 * rows exist. `collectionRate` keeps its null (nothing billed) distinct from
 * absence (not reported); every other member figure that is absent stays
 * undefined rather than becoming a zero.
 */
function normalizeGroupFinancial(root: unknown): GroupFinancial | undefined {
  if (!isRecord(root) || !isRecord(root.financial)) return undefined;
  const financial = root.financial;

  const institutions = Array.isArray(financial.institutions)
    ? financial.institutions
        .filter(
          (entry): entry is Record<string, unknown> & { institutionId: string } =>
            isRecord(entry) && typeof entry.institutionId === 'string',
        )
        .map((entry) => ({
          institutionId: entry.institutionId,
          available: entry.available === true,
          students: optionalCount(entry.students),
          staff: optionalCount(entry.staff),
          parents: optionalCount(entry.parents),
          classGroups: optionalCount(entry.classGroups),
          payments: optionalCount(entry.payments),
          expectedFees: optionalCount(entry.expectedFees),
          collectedFees: optionalCount(entry.collectedFees),
          outstandingFees: optionalCount(entry.outstandingFees),
          collectionRate: optionalNullableCount(entry.collectionRate),
          currency: optionalString(entry.currency),
          // Academic aggregates EduCore relays through core. null means sent
          // but not reported; the finance tab renders it as an em dash, the
          // same as a figure that was never sent.
          admissions: nullableAcademicObject(entry.admissions, [
            'applications',
            'offered',
            'accepted',
            'enrolled',
            'conversionRate',
          ]),
          capacity: nullableAcademicObject(entry.capacity, [
            'configuredClassGroups',
            'totalCapacity',
            'totalEnrolled',
            'occupancyRate',
          ]),
          attendanceRate: optionalNullableCount(entry.attendanceRate),
          progression: nullableAcademicObject(entry.progression, [
            'eligibleCohort',
            'continuedWithinGroup',
            'didNotContinueWithinGroup',
            'unknownDestination',
            'internalProgressionRate',
          ]),
          lastUpdatedAt: optionalString(entry.lastUpdatedAt),
        }))
    : [];

  const totalsSource = isRecord(financial.totals) ? financial.totals : undefined;
  // The academic totals are all-or-nothing: each sub-object only exists when
  // every count core requires for it was reported by every available member.
  const totalsAdmissions = isRecord(totalsSource?.admissions)
    ? groupTotalsSubObject(totalsSource.admissions, ['applications', 'offered', 'accepted', 'enrolled'], [
        'conversionRate',
      ])
    : undefined;
  const totalsCapacity = isRecord(totalsSource?.capacity)
    ? groupTotalsSubObject(totalsSource.capacity, ['configuredClassGroups', 'totalCapacity', 'totalEnrolled'], [
        'occupancyRate',
      ])
    : undefined;
  const totalsProgression = isRecord(totalsSource?.progression)
    ? groupTotalsSubObject(
        totalsSource.progression,
        ['eligibleCohort', 'continuedWithinGroup', 'didNotContinueWithinGroup', 'unknownDestination'],
        ['internalProgressionRate'],
      )
    : undefined;

  const totals =
    isRecord(financial.totals) && (financial.totals.students !== undefined || financial.totals.expectedFees !== undefined)
      ? {
          students: toCount(financial.totals.students),
          staff: toCount(financial.totals.staff),
          // The counts are optional on the totals too: core omits them unless
          // EVERY member reported them, and absence must stay absence so the
          // group surface reads "not reported", never a confident zero.
          ...(optionalCount(financial.totals.parents) !== undefined
            ? { parents: optionalCount(financial.totals.parents) }
            : {}),
          ...(optionalCount(financial.totals.classGroups) !== undefined
            ? { classGroups: optionalCount(financial.totals.classGroups) }
            : {}),
          ...(optionalCount(financial.totals.payments) !== undefined
            ? { payments: optionalCount(financial.totals.payments) }
            : {}),
          expectedFees: toCount(financial.totals.expectedFees),
          collectedFees: toCount(financial.totals.collectedFees),
          outstandingFees: toCount(financial.totals.outstandingFees),
          // The totals object is all-or-nothing: a rate core did not compute is
          // reported as null (nothing billed), never left as an absent hole.
          collectionRate: optionalNullableCount(financial.totals.collectionRate) ?? null,
          ...(totalsAdmissions !== undefined ? { admissions: totalsAdmissions } : {}),
          ...(totalsCapacity !== undefined ? { capacity: totalsCapacity } : {}),
          ...(totalsProgression !== undefined ? { progression: totalsProgression } : {}),
        }
      : null;

  return {
    available: financial.available === true,
    totals,
    institutions,
  };
}

/**
 * POST /platform/structure/groups/:groupId/products.
 *
 * The bulk product result is not a single row — see BulkProductApplyResult. The
 * response body must name the product and the action, and every skipped member
 * must say why; rows that cannot say why are dropped rather than rendered as a
 * mystery the operator has to chase. `missingInstitutions` is tolerated as an
 * absent list because an older core may answer with only applied/skipped.
 */
function normalizeBulkProductResult(payload: unknown): BulkProductApplyResult {
  const root = isRecord(payload) ? payload : {};
  const productCode = optionalString(root.productCode);
  const action = optionalString(root.action);
  if (
    !productCode ||
    !action ||
    !BULK_PRODUCT_ACTIONS.includes(action as (typeof BULK_PRODUCT_ACTIONS)[number])
  ) {
    throw new Error('The server returned a bulk product result this app cannot read.');
  }
  return {
    productCode,
    action: action as BulkProductAction,
    applied: rowsWith<BulkProductAppliedEntry>(root.applied, ['institutionId']),
    skipped: rowsWith<BulkProductSkippedEntry>(root.skipped, ['institutionId', 'reason']),
    missingInstitutions: stringList(root.missingInstitutions),
  };
}

function body(payload: unknown): RequestInit {
  return { method: 'POST', body: JSON.stringify(payload) };
}

function patch(payload: unknown): RequestInit {
  return { method: 'PATCH', body: JSON.stringify(payload) };
}

/**
 * DELETE with a body.
 *
 * Revoking a policy override is a DELETE that carries the reason, because the row
 * is end-dated rather than removed and the reason is the point of the record. The
 * verb is the server's choice and this client follows it rather than asking for a
 * second route that would mean the same thing.
 */
function del(payload?: unknown): RequestInit {
  return payload === undefined
    ? { method: 'DELETE' }
    : { method: 'DELETE', body: JSON.stringify(payload) };
}

/* ─── Paging, single objects and query building ─── */

/**
 * A page of rows, tolerant of a service that answers with a bare array.
 *
 * `PaginatedResult` is what identity returns, but not every route is paginated and
 * some are still being converted. A bare array is treated as a single complete
 * page rather than as a failure, because the alternative — refusing to render a
 * list that arrived perfectly well — is worse than a total that equals what is on
 * screen. What is never done is inventing a total: if the server did not say how
 * many there are, the count shown is the count returned.
 */
function normalizePage<T extends object>(payload: unknown, required: readonly (keyof T & string)[]): Page<T> {
  if (Array.isArray(payload)) {
    const rows = rowsWith<T>(payload, required);
    return { data: rows, total: rows.length, page: 1, limit: rows.length, totalPages: 1 };
  }
  if (!isRecord(payload)) {
    return { data: [], total: 0, page: 1, limit: 0, totalPages: 0 };
  }
  const rows = rowsWith<T>(unwrap(payload, 'data'), required);
  const total = typeof payload.total === 'number' ? payload.total : rows.length;
  const limit = typeof payload.limit === 'number' && payload.limit > 0 ? payload.limit : rows.length;
  return {
    data: rows,
    total,
    page: typeof payload.page === 'number' ? payload.page : 1,
    limit,
    totalPages:
      typeof payload.totalPages === 'number'
        ? payload.totalPages
        : limit > 0
          ? Math.max(1, Math.ceil(total / limit))
          : 1,
  };
}

/**
 * One object, or a thrown error naming the field that was missing.
 *
 * Returning a half-built object here would push the failure into a component that
 * dereferences it three renders later, where the stack says nothing about which
 * endpoint lied. Failing at the boundary means AsyncPanel can say which call broke.
 */
function objectWith<T extends object>(
  payload: unknown,
  required: readonly (keyof T & string)[],
  ...envelopeKeys: string[]
): T {
  const root = unwrapObject(payload, envelopeKeys);
  if (!isRecord(root)) {
    throw new Error('The server returned a response this app cannot read.');
  }
  const missing = required.filter((key) => root[key] === undefined || root[key] === null);
  if (missing.length > 0) {
    throw new Error(`The server response is missing: ${missing.join(', ')}.`);
  }
  return root as unknown as T;
}

/**
 * Peels Core's response envelope, then the caller's own projection key.
 *
 * THE ENVELOPE STEP IS WHY EVERY SINGLE-OBJECT MUTATION USED TO FAIL.
 * campusone-core-service installs ResponseInterceptor globally in main.ts, so every
 * response — read or write — arrives as
 * `{ success: true, data: <the thing>, timestamp, latency, correlationId }`.
 *
 * This function only ever tried the keys its caller passed. `objectWith(payload,
 * ['id', 'code', 'label'], 'item')` therefore looked for `payload.item`, found
 * nothing, returned the whole envelope, and reported the three fields as missing —
 * because they are missing from an envelope. The error named the fields and so
 * read like a backend that had stopped sending them, which is why it survived: the
 * response was correct all along.
 *
 * It went unnoticed on reads because their two paths both handle the envelope
 * already — `unwrap` falls back through `['items', 'data', 'results']`, and
 * `normalizeCaseDetail` walks `root.data` explicitly. Only `objectWith` was left
 * out, so the failure landed exactly on the mutations: ticking a checklist item,
 * activating a product, granting support access, saving a flag, registering an
 * integration. Around two dozen call sites, one cause.
 *
 * The envelope is matched on `success === true` rather than by appending 'data' to
 * the key list, because some of these records could legitimately carry a `data`
 * property of their own; only the interceptor pairs it with `success`.
 */
function unwrapObject(payload: unknown, keys: readonly string[]): unknown {
  if (!isRecord(payload)) return payload;

  const root = payload.success === true && isRecord(payload.data) ? payload.data : payload;
  if (!isRecord(root)) return root;

  // Then the caller's projection — `{ item: ... }`, `{ grant: ... }` — which some
  // endpoints nest INSIDE the envelope and older ones returned bare. Checked after
  // the envelope so both shapes resolve to the same record.
  for (const key of keys) {
    if (isRecord(root[key])) return root[key];
  }
  return root;
}

type QueryValue = string | number | boolean | readonly string[] | undefined | null;

/**
 * Builds a query string, dropping anything empty.
 *
 * Empty values are omitted rather than sent blank: `?institutionId=` is a filter on
 * the empty string in several of these services, which returns nothing and looks
 * exactly like "there is nothing to report".
 */
function query(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      search.set(key, value.join(','));
      continue;
    }
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

function pageParams(request: PageRequest): Record<string, QueryValue> {
  return { page: request.page, limit: request.limit, sort: request.sort, order: request.order };
}

const seg = encodeURIComponent;

/* ─── PA-14 ─── */

const AUDIT_KEYS = ['id', 'eventType', 'outcome', 'occurredAt'] as const;

/** Which route backs each dedicated view. The server owns what each one contains. */
const AUDIT_VIEW_PATHS: Record<AuditView, string> = {
  EVENTS: 'events',
  DENIALS: 'denials',
  CROSS_TENANT: 'cross-tenant-denials',
  ROLE_ELEVATIONS: 'role-elevations',
  SUSPENSIONS: 'suspensions',
  PRODUCT_CHANGES: 'product-activations',
};

function auditQuery(filters: AuditSearchFilters): string {
  return query({
    actorPersonId: filters.actorPersonId,
    actorKeycloakUserId: filters.actorKeycloakUserId,
    institutionId: filters.institutionId,
    tenantId: filters.tenantId,
    eventTypes: filters.eventTypes,
    targetType: filters.targetType,
    targetId: filters.targetId,
    outcomes: filters.outcomes,
    from: filters.from,
    to: filters.to,
    correlationId: filters.correlationId,
    ...pageParams(filters),
  });
}

/* ─── PA-05 ─── */

const FLAG_KEYS = ['id', 'featureCode', 'scope'] as const;

function normalizeResolvedFlag(payload: unknown, featureCode: string): ResolvedFeatureFlag {
  const root = isRecord(payload) ? payload : {};
  return {
    featureCode: typeof root.featureCode === 'string' ? root.featureCode : featureCode,
    // A feature with no setting anywhere resolves to disabled — that is the server's
    // rule, and defaulting to `true` here would show a feature as on because the
    // response was unreadable.
    enabled: root.enabled === true,
    resolvedFromScope:
      typeof root.resolvedFromScope === 'string' ? (root.resolvedFromScope as FeatureFlagScope) : null,
    resolvedFromScopeValue:
      typeof root.resolvedFromScopeValue === 'string' ? root.resolvedFromScopeValue : null,
    config: isRecord(root.config) ? root.config : null,
    version: typeof root.version === 'number' ? root.version : null,
  };
}

/**
 * What the onboarding-case list endpoint actually accepts.
 *
 * Exactly these fields, because the controller validates with
 * `forbidNonWhitelisted` — an unrecognised query parameter is a 400, not a
 * silently ignored filter. This console previously sent `q=` for the search box,
 * which meant every search returned "the request failed" rather than results, and
 * the failure looked like an outage. Free-text search is applied client-side until
 * the endpoint offers it; see OnboardingCasesPage, which says so on screen.
 */
export interface OnboardingCaseFilters {
  state?: OnboardingState | 'ALL';
  institutionType?: InstitutionType;
  onboardingOwnerId?: string;
  countryCode?: string;
}

function caseQuery(filters: OnboardingCaseFilters): string {
  return query({
    state: filters.state && filters.state !== 'ALL' ? filters.state : undefined,
    institutionType: filters.institutionType,
    onboardingOwnerId: filters.onboardingOwnerId,
    countryCode: filters.countryCode,
  });
}

/* ─── PA-13 ─── */

/**
 * The health snapshot, with the honesty block preserved rather than flattened.
 *
 * A missing `honesty` is replaced with an empty one and a statement saying so —
 * never with silence. Silence reads as "everything here is measured", which is the
 * single claim this endpoint exists to avoid making. Likewise `overall` defaults
 * to UNKNOWN rather than to HEALTHY when the body cannot be read.
 */
function normalizeHealth(payload: unknown): PlatformHealthSnapshot {
  const root = isRecord(payload) ? payload : {};
  const honesty = isRecord(root.honesty) ? root.honesty : {};
  const signals = rowsWith<OperationalSignal>(root.signals, ['key', 'title', 'state', 'basis']).map(
    (signal) => ({
      ...signal,
      measurements: isRecord(signal.measurements)
        ? (signal.measurements as Record<string, number | string | null>)
        : {},
    }),
  );

  return {
    overall:
      typeof root.overall === 'string' ? (root.overall as PlatformHealthSnapshot['overall']) : 'UNKNOWN',
    signals,
    openIncidents: toCount(root.openIncidents),
    activeMaintenanceWindows: toCount(root.activeMaintenanceWindows),
    generatedAt: optionalString(root.generatedAt) ?? '',
    honesty: {
      measuredHere: stringList(honesty.measuredHere),
      notReporting: stringList(honesty.notReporting),
      notMeasurable: stringList(honesty.notMeasurable),
      statement:
        optionalString(honesty.statement) ??
        'This service did not state which of these signals it actually measures.',
    },
  };
}

/* ─── PA-11 ─── */

/**
 * The resolved policy set for one institution.
 *
 * Every policy carries its source, and a suppressed override is kept rather than
 * dropped: an institution whose override fell below a floor CSKY raised since it
 * was granted needs to see that it stopped applying and why. A filtered list would
 * show the platform default with nothing saying it had ever been different.
 */
function normalizeResolvedPolicies(payload: unknown): ResolvedPolicySet {
  const root = isRecord(payload) ? payload : {};
  if (typeof root.institutionId !== 'string') {
    throw new Error('The server returned a policy set with no institution.');
  }
  return {
    institutionId: root.institutionId,
    asOf: optionalString(root.asOf) ?? '',
    platformVersionNumber:
      typeof root.platformVersionNumber === 'number' ? root.platformVersionNumber : null,
    policies: rowsWith<ResolvedPolicy>(unwrap(root, 'policies'), ['key', 'source'], ['value']),
  };
}

/* ─── Platform connections and institution mappings ─── */

/**
 * The two route roots for the connection registry, in one place.
 *
 * Transcribed from PlatformConnectionController and
 * InstitutionIntegrationMappingController in campusone-core-service. Kept as
 * constants because they are the paths most likely to move, and when one does the
 * correction should be a single edit here plus a failing assertion in
 * test/platform-routes.test.ts — not eleven call sites and a screen that has been
 * quietly claiming a shipped feature was never deployed.
 *
 * The mapping root is `integration-mappings`, not `integrations`: the institution
 * console's own read-only view is mounted at `institutions/:id/integration-status`
 * with no platform prefix, and the two must not be confusable.
 */
const CONNECTIONS_PATH = '/platform/connections';

const institutionMappingsPath = (institutionId: string): string =>
  `/platform/institutions/${seg(institutionId)}/integration-mappings`;

/**
 * Fills in the two array fields the UI iterates over.
 *
 * A connection whose `capabilities` arrived as null or as a string would reach
 * `.map()` and blank the page. It is normalised to an empty array rather than
 * dropped, because a connection with no readable capability list is still a
 * connection an operator needs to see — and an empty capability list is a visible,
 * actionable fact in its own right.
 */
function normalizeConnection(connection: PlatformConnection): PlatformConnection {
  return {
    ...connection,
    capabilities: capabilityList(connection.capabilities),
    retryPolicy: isRecord(connection.retryPolicy)
      ? {
          maxAttempts: toCount(connection.retryPolicy.maxAttempts),
          backoffMs: toCount(connection.retryPolicy.backoffMs),
          retryOn: Array.isArray(connection.retryPolicy.retryOn)
            ? connection.retryPolicy.retryOn.filter(
                (status): status is number => typeof status === 'number',
              )
            : [],
        }
      : null,
  };
}

function normalizeMapping(mapping: InstitutionIntegrationMapping): InstitutionIntegrationMapping {
  return {
    ...mapping,
    capabilities: capabilityList(mapping.capabilities),
    connection: isRecord(mapping.connection)
      ? normalizeConnection(mapping.connection as PlatformConnection)
      : undefined,
  };
}

/**
 * Keeps only capability codes this build knows.
 *
 * A code minted after this bundle shipped is dropped rather than rendered, because
 * an unresolvable capability chip is a label an operator cannot act on and cannot
 * distinguish from a typo. The connection itself is never dropped over one.
 */
function capabilityList(value: unknown): IntegrationCapability[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is IntegrationCapability =>
    (INTEGRATION_CAPABILITIES as readonly string[]).includes(entry as string),
  );
}

export const platformApi = {
  /** PA-01. One projection: partial sections render as unavailable, not as zero. */
  dashboard: () => core<unknown>('/platform/dashboard').then(normalizeDashboard),

  onboarding: {
    /** PA-02 list. Filtering is server-side so a state filter is not a lie on page 2. */
    listCases: (filters: OnboardingCaseFilters = {}) =>
      core<unknown>(`/platform/onboarding/cases${caseQuery(filters)}`).then(normalizeCaseList),
    getCase: (caseId: string) =>
      core<unknown>(`/platform/onboarding/cases/${seg(caseId)}`).then(normalizeCaseDetail),
    previewCode: (legalName: string, countryCode: string) =>
      core<unknown>(`/platform/onboarding/cases/code-preview${query({ legalName, countryCode })}`).then(
        normalizeCodePreview,
      ),
    createCase: (payload: CreateOnboardingCaseRequest) =>
      core<unknown>(
        '/platform/onboarding/cases',
        body({
          legalName: payload.institutionName,
          displayName: payload.institutionName,
          institutionType: payload.institutionType,
          countryCode: payload.countryCode,
          timezone: payload.countryCode === 'CM' ? 'Africa/Douala' : 'UTC',
          contactEmail: payload.contactEmail,
          primaryContactEmail: payload.contactEmail,
          primaryContactName: payload.contactName || payload.institutionName,
          legalRegistrationNumber: payload.externalReference,
          metadata: payload.notes ? { notes: payload.notes } : undefined,
        }),
      ).then(normalizeCaseDetail),

    /**
     * What would block a move, asked before it is attempted.
     *
     * The state machine says which moves are shaped correctly; only the server
     * knows whether a checklist stage is outstanding or an action is still open.
     * Asking first is what turns "the transition was refused" into "these two
     * things are in the way".
     */
    readiness: (caseId: string, targetState: OnboardingState) =>
      core<unknown>(`/platform/onboarding/cases/${seg(caseId)}/readiness${query({ targetState })}`).then(
        (payload) => {
          const root = isRecord(payload) ? payload : {};
          return {
            currentState: (typeof root.currentState === 'string'
              ? root.currentState
              : targetState) as OnboardingState,
            targetState,
            transitionAllowed: root.transitionAllowed === true,
            reasonRequired: root.reasonRequired !== false,
            incompleteStages: stringList(root.incompleteStages),
            blockingActions: rowsWith<{ id: string; title: string }>(root.blockingActions, ['id', 'title']),
          } satisfies OnboardingReadiness;
        },
      ),

    transition: (caseId: string, payload: TransitionOnboardingCaseRequest) =>
      core<unknown>(`/platform/onboarding/cases/${seg(caseId)}/transitions`, body(payload)).then(
        normalizeCaseDetail,
      ),

    /**
     * Ticks one checklist item. PATCH, by the row's id.
     *
     * Core mounts `PATCH :id/checklist/:itemId` and the id is a UUID. This console
     * sent `POST :id/checklist/:code`, which no service has ever served, so every
     * tick returned 404 and the box sprang back citing a missing endpoint. The
     * response is the item, not the case, so the caller refreshes the case itself.
     */
    setChecklistItem: (caseId: string, itemId: string, completed: boolean, notes?: string) =>
      core<unknown>(
        `/platform/onboarding/cases/${seg(caseId)}/checklist/${seg(itemId)}`,
        patch({ completed, notes }),
      ).then((payload) => objectWith<OnboardingChecklistItem>(payload, ['id', 'code', 'label'], 'item')),
  },

  /**
   * The product catalogue, independent of any institution.
   *
   * Served by core's own `/products`, which every authenticated caller may read — it is
   * reference data, the same for every institution, and carries nothing an operator should
   * not see. It is fetched rather than mirrored as a constant in this app so that a product
   * added to the catalogue appears in the picker without a front-end release.
   */
  products: {
    catalogue: () => core<unknown>('/products').then(normalizeCatalogue),
  },

  institutions: {
    /**
     * The institution register — GET /platform/institutions, served by core's
     * InstitutionDirectoryController. (An older note here said core served no
     * list; the directory shipped since.) Besides InstitutionsPage, this feeds
     * InstitutionPicker, which every platform screen uses instead of asking
     * operators to paste institution UUIDs.
     */
    list: (search?: string) =>
      core<unknown>(`/platform/institutions${query({ q: search })}`).then(normalizeInstitutionList),
    get: (institutionId: string) =>
      core<unknown>(`/platform/institutions/${seg(institutionId)}`).then(normalizeInstitutionDetail),

    /** PA-04. */
    products: (institutionId: string) =>
      core<unknown>(`/platform/institutions/${seg(institutionId)}/products`).then(normalizeProducts),

    /** Product codes users of this institution can currently reach. */
    accessibleProducts: (institutionId: string) =>
      core<unknown>(`/platform/institutions/${seg(institutionId)}/products/accessible`).then((payload) =>
        isRecord(payload) ? stringList(payload.productCodes) : [],
      ),

    /**
     * Subscribes the institution to a product it has never held.
     *
     * Distinct from a transition: there is no activation row to move yet. Core puts
     * the new row in PENDING_CONFIGURATION, which is the only state NOT_SUBSCRIBED
     * leads to.
     */
    subscribeProduct: (institutionId: string, payload: SubscribeProductRequest) =>
      core<unknown>(`/platform/institutions/${seg(institutionId)}/products`, body(payload)).then((result) =>
        objectWith<InstitutionProductAssignment>(result, ['productCode', 'state'], 'activation'),
      ),

    /**
     * Changes a product's state. One endpoint, with the target state in the body.
     *
     * The console used to POST to /activate, /suspend and /disable, none of which
     * core has ever mounted — three 404s for a capability that shipped. The
     * transition is also the better contract: which states are reachable is a
     * property of the state machine, not of how many verbs somebody wrote, and a
     * new state does not need a new route. Core returns the single updated
     * activation, so callers refresh the list rather than being handed one.
     */
    transitionProduct: (
      institutionId: string,
      productCode: string,
      payload: TransitionProductStateRequest,
    ) =>
      core<unknown>(
        `/platform/institutions/${seg(institutionId)}/products/${seg(productCode)}/transitions`,
        body(payload),
      ).then((result) =>
        objectWith<InstitutionProductAssignment>(result, ['productCode', 'state'], 'activation'),
      ),

    /** Amends dates, trial, launch URL or configuration status without moving state. */
    updateProduct: (
      institutionId: string,
      productCode: string,
      payload: Omit<SubscribeProductRequest, 'productCode'>,
    ) =>
      core<unknown>(
        `/platform/institutions/${seg(institutionId)}/products/${seg(productCode)}`,
        patch(payload),
      ).then((result) =>
        objectWith<InstitutionProductAssignment>(result, ['productCode', 'state'], 'activation'),
      ),

    /* ─── PA-08 lifecycle ─── */

    /** Current lifecycle state and suspension scope. */
    lifecycle: (institutionId: string) =>
      core<unknown>(`/platform/institutions/${seg(institutionId)}/lifecycle`).then((payload) =>
        objectWith<InstitutionLifecycle>(payload, ['institutionId', 'state'], 'lifecycle'),
      ),

    /**
     * Which actions are legal right now, asked rather than inferred.
     *
     * The console used to derive the buttons from an onboarding state, which is a
     * different state machine on a different entity — that is how it came to offer
     * RESUME, an action core has no name for, and to hide ENTER_MAINTENANCE and
     * ARCHIVE, which it has always had.
     */
    lifecycleActions: (institutionId: string) =>
      core<unknown>(`/platform/institutions/${seg(institutionId)}/lifecycle/actions`).then((payload) => {
        const root = isRecord(payload) ? payload : {};
        return stringList(root.actions) as LifecycleAction[];
      }),

    /**
     * PA-08. The impact summary is fetched before the dialog can be confirmed —
     * only the server knows what sits behind an institution id.
     */
    lifecycleImpact: (
      institutionId: string,
      action: LifecycleAction,
      suspensionScope?: string,
      suspendedProductCode?: string,
    ) =>
      core<unknown>(
        `/platform/institutions/${seg(institutionId)}/lifecycle/impact` +
          query({ action, suspensionScope, suspendedProductCode }),
      ).then((payload) => {
        const root = isRecord(payload) ? payload : {};
        return {
          institutionId,
          action,
          currentState: (typeof root.currentState === 'string'
            ? root.currentState
            : 'PENDING_ACTIVATION') as LifecycleImpact['currentState'],
          resultingState: (typeof root.resultingState === 'string'
            ? root.resultingState
            : 'PENDING_ACTIVATION') as LifecycleImpact['resultingState'],
          suspensionScope: (typeof root.suspensionScope === 'string'
            ? root.suspensionScope
            : null) as LifecycleImpact['suspensionScope'],
          productsLosingAccess: stringList(root.productsLosingAccess),
          productsRetainingAccess: stringList(root.productsRetainingAccess),
          enabledFeatureFlagCount: toCount(root.enabledFeatureFlagCount),
          campusCount: toCount(root.campusCount),
          branchCount: toCount(root.branchCount),
          liveIntegrationCount: toCount(root.liveIntegrationCount),
          warnings: stringList(root.warnings),
        } satisfies LifecycleImpact;
      }),

    /**
     * Performs the action. `POST .../lifecycle/actions`, not `.../lifecycle`.
     *
     * Returns the new lifecycle AND the event that produced it, and both are handed
     * back: the event carries the impact summary the operator was shown at the
     * moment they confirmed, which is the record of what they were told.
     */
    applyLifecycleAction: (institutionId: string, payload: LifecycleActionRequest) =>
      core<unknown>(`/platform/institutions/${seg(institutionId)}/lifecycle/actions`, body(payload)).then(
        (result) => {
          const root = isRecord(result) ? result : {};
          return {
            lifecycle: objectWith<InstitutionLifecycle>(root.lifecycle, ['institutionId', 'state']),
            event: objectWith<InstitutionLifecycleEvent>(root.event, ['id', 'action', 'toState']),
          } satisfies LifecycleActionResult;
        },
      ),

    /** Lifecycle history, with the impact that was shown at each decision. */
    lifecycleEvents: (institutionId: string) =>
      core<unknown>(`/platform/institutions/${seg(institutionId)}/lifecycle/events`).then((payload) =>
        rowsWith<InstitutionLifecycleEvent>(unwrap(payload, 'events'), ['id', 'action', 'toState']),
      ),

    /** Reclassifies the institution and announces it to every consumer. */
    changeType: (institutionId: string, institutionType: InstitutionType, reason: string) =>
      core<unknown>(
        `/platform/institutions/${seg(institutionId)}/lifecycle/institution-type`,
        body({ institutionType, reason }),
      ).then(normalizeInstitutionDetail),
  },

  /**
   * Who the operator is, as the identity service knows them.
   *
   * Needed because two write paths — approving a delegation and revoking one —
   * take the acting person's UUID in the body rather than deriving it from the
   * token. The token subject is a Keycloak id and is not the same value, so the
   * console asks the service which person this session belongs to instead of
   * guessing. The response is read defensively: this endpoint predates the platform
   * work and its field naming has moved.
   */
  me: () =>
    identity<unknown>('/me').then((payload) => {
      const root = isRecord(payload) ? payload : {};
      return {
        personId: optionalString(root.personId) ?? optionalString(root.id),
        identityId: optionalString(root.identityId),
        email: optionalString(root.email),
        name: optionalString(root.name),
      };
    }),

  /**
   * PA-07. Identity's, because people and sign-in accounts are identity's.
   *
   * The paths are the ones PlatformAdministratorsController actually mounts —
   * `/platform/administrators/invitations`, with the institution as a query
   * parameter. The SAME routes serve group invitations: identity's DTO gained
   * an optional groupId in the identities phase, and a request carries
   * whichever of the two scopes it is for. `list`/`invite` therefore take an
   * institutionId, a groupId, or neither, and the console passes `undefined`
   * for the scope that does not apply.
   */
  administrators: {
    list: (institutionId?: string, groupId?: string) =>
      identity<unknown>(
        `/platform/administrators/invitations${query({ ...(institutionId ? { institutionId } : {}), ...(groupId ? { groupId } : {}) })}`,
      ).then((payload) => ({
        invitations: rowsWith<AdministratorInvitation>(unwrap(payload, 'invitations'), [
          'id',
          'email',
          'status',
        ]),
      })),
    invite: (institutionId: string | undefined, payload: InviteAdministratorRequest, groupId?: string) =>
      identity<unknown>(
        '/platform/administrators/invitations',
        body({ ...payload, institutionId, groupId }),
      ).then(normalizeInvitation),
    resend: (invitationId: string) =>
      identity<unknown>(`/platform/administrators/invitations/${seg(invitationId)}/resend`, body({})).then(
        normalizeInvitation,
      ),
    revoke: (invitationId: string, reason: string) =>
      identity<unknown>(
        `/platform/administrators/invitations/${seg(invitationId)}/revoke`,
        body({ reason }),
      ).then(normalizeInvitation),
  },

  /**
   * PA-14. The governance surface.
   *
   * Every dedicated view is its own route because the server decides which event
   * types make up a role elevation or a cross-tenant denial. Approximating that
   * client-side gives a report that omits whatever was added since this build
   * shipped, and an audit report that silently under-reports is worse than none:
   * it reads as a clean bill of health.
   */
  audit: {
    search: (view: AuditView, filters: AuditSearchFilters = {}) =>
      identity<unknown>(`/platform/audit/${AUDIT_VIEW_PATHS[view]}${auditQuery(filters)}`).then((payload) =>
        normalizePage<AuditEvent>(payload, AUDIT_KEYS),
      ),

    /** Ascending, because before/after values only read forwards. */
    historyForTarget: (targetType: string, targetId: string) =>
      identity<unknown>(`/platform/audit/targets/${seg(targetType)}/${seg(targetId)}`).then((payload) =>
        rowsWith<AuditEvent>(unwrap(payload, 'events'), AUDIT_KEYS),
      ),

    /**
     * Governance actions recorded by core rather than identity.
     *
     * Core keeps its own append-only stream for the decisions it owns. A reviewer
     * should not have to know which service made a decision in order to find it,
     * so both are offered — but they come back as DIFFERENT shapes and are typed
     * that way. A core row has an `action` and a `resourceType`, no outcome and no
     * approval chain; typing it as identity's AuditEvent put four empty columns and
     * an outcome it never had on screen.
     */
    forInstitution: (institutionId: string, limit = 100) =>
      core<unknown>(`/platform/audit/institutions/${seg(institutionId)}${query({ limit })}`).then((payload) =>
        rowsWith<PlatformAuditRecord>(unwrap(payload, 'records'), ['id', 'action', 'occurredAt']),
      ),

    /** Everything that happened to one resource, core's side. */
    forResource: (resourceType: string, resourceId: string, limit = 100) =>
      core<unknown>(`/platform/audit/resources/${seg(resourceType)}/${seg(resourceId)}${query({ limit })}`).then(
        (payload) => rowsWith<PlatformAuditRecord>(unwrap(payload, 'records'), ['id', 'action', 'occurredAt']),
      ),
  },

  /** PA-12. Identity's: delegation is a statement about roles and permissions. */
  delegations: {
    list: (institutionId: string) =>
      identity<unknown>(`/platform/delegations${query({ institutionId })}`).then((payload) =>
        rowsWith<Delegation>(unwrap(payload, 'delegations'), ['id', 'status']),
      ),
    grant: (payload: GrantDelegationRequest) =>
      identity<unknown>('/platform/delegations', body(payload)).then((result) =>
        objectWith<Delegation>(result, ['id', 'status'], 'delegation'),
      ),
    approve: (delegationId: string, approverPersonId: string) =>
      identity<unknown>(`/platform/delegations/${seg(delegationId)}/approve`, body({ approverPersonId })).then(
        (result) => objectWith<Delegation>(result, ['id', 'status'], 'delegation'),
      ),
    revoke: (delegationId: string, revokedByPersonId: string, reason: string) =>
      identity<unknown>(
        `/platform/delegations/${seg(delegationId)}/revoke`,
        body({ revokedByPersonId, reason }),
      ).then((result) => objectWith<Delegation>(result, ['id', 'status'], 'delegation')),

    /**
     * What the delegation actually confers.
     *
     * Fetched rather than inferred from the role code, because the answer depends
     * on the permission rows behind that role in this environment, and a UI that
     * guessed would be describing a different grant from the one being made.
     */
    review: (delegationId: string) =>
      identity<unknown>(`/platform/delegations/${seg(delegationId)}/inherited-permissions`).then((payload) =>
        objectWith<InheritedPermissionReview>(payload, ['delegationId'], 'review'),
      ),
  },

  /** PA-17. Time-limited access into one institution, never silent. */
  supportAccess: {
    /** With an institution, its grants. Without one, everything awaiting approval. */
    list: (institutionId?: string) =>
      identity<unknown>(`/platform/support-access/requests${query({ institutionId })}`).then((payload) =>
        rowsWith<SupportAccessGrant>(unwrap(payload, 'grants'), ['id', 'status', 'mode']),
      ),
    get: (grantId: string) =>
      identity<unknown>(`/platform/support-access/requests/${seg(grantId)}`).then((payload) =>
        objectWith<SupportAccessGrant>(payload, ['id', 'status', 'mode'], 'grant'),
      ),
    request: (payload: RequestSupportAccessRequest) =>
      identity<unknown>('/platform/support-access/requests', body(payload)).then((result) =>
        objectWith<SupportAccessGrant>(result, ['id', 'status', 'mode'], 'grant'),
      ),
    approve: (grantId: string) =>
      identity<unknown>(`/platform/support-access/requests/${seg(grantId)}/approve`, body({})).then((result) =>
        objectWith<SupportAccessGrant>(result, ['id', 'status', 'mode'], 'grant'),
      ),
    deny: (grantId: string, reason: string) =>
      identity<unknown>(`/platform/support-access/requests/${seg(grantId)}/deny`, body({ reason })).then(
        (result) => objectWith<SupportAccessGrant>(result, ['id', 'status', 'mode'], 'grant'),
      ),

    /**
     * Opens the session and returns the terms it runs under.
     *
     * `impersonationBanner` is forced true here regardless of what arrives. The
     * server always sends true and has no way to send anything else, but this is
     * the one field where a missing or malformed value must not be able to produce
     * a session with nothing on screen saying somebody is being impersonated.
     */
    start: (grantId: string) =>
      identity<unknown>(`/platform/support-access/requests/${seg(grantId)}/start`, body({})).then((payload) => {
        const descriptor = objectWith<SupportSessionDescriptor>(
          payload,
          ['grantId', 'mode', 'institutionId', 'expiresAt'],
          'session',
        );
        return { ...descriptor, impersonationBanner: true, readOnly: descriptor.readOnly !== false };
      }),
    end: (grantId: string) =>
      identity<unknown>(`/platform/support-access/requests/${seg(grantId)}/end`, body({})).then((result) =>
        objectWith<SupportAccessGrant>(result, ['id', 'status', 'mode'], 'grant'),
      ),

    /** Every action taken under a grant, not a sample. */
    recordAction: (grantId: string, action: string, detail?: Record<string, unknown>) =>
      identity<unknown>(
        `/platform/support-access/requests/${seg(grantId)}/actions`,
        body({ action, detail }),
      ).then(() => undefined),
  },

  /** PA-05. Scoped feature configuration, with the precedence made visible. */
  featureFlags: {
    list: (institutionId: string) =>
      core<unknown>(`/platform/institutions/${seg(institutionId)}/feature-flags`).then((payload) =>
        rowsWith<FeatureFlagSetting>(unwrap(payload, 'settings'), FLAG_KEYS),
      ),
    set: (institutionId: string, payload: SetFeatureFlagRequest) =>
      core<unknown>(`/platform/institutions/${seg(institutionId)}/feature-flags`, body(payload)).then((result) =>
        objectWith<FeatureFlagSetting>(result, ['id', 'featureCode', 'scope'], 'setting'),
      ),
    resolve: (
      institutionId: string,
      featureCode: string,
      context: { campusId?: string; role?: string; academicYearId?: string } = {},
    ) =>
      core<unknown>(
        `/platform/institutions/${seg(institutionId)}/feature-flags/${seg(featureCode)}/resolve${query(context)}`,
      ).then((payload) => normalizeResolvedFlag(payload, featureCode)),
    versions: (institutionId: string, featureCode: string) =>
      core<unknown>(
        `/platform/institutions/${seg(institutionId)}/feature-flags/${seg(featureCode)}/versions`,
      ).then((payload) => rowsWith<FeatureFlagVersion>(unwrap(payload, 'versions'), ['id', 'version'])),

    /**
     * The feature catalogue, with each feature's declared dependencies.
     *
     * This is deployed — FeatureCatalogueController mounts `GET /platform/features`
     * and answers `{ features: [...] }`. The console described it as missing, which
     * was true when the comment was written and had stopped being true by the time
     * anyone read it. `rulesReadable` is carried through: false with an empty
     * `requires` means the rules could not be parsed, which is a different claim
     * from "this feature has no dependencies", and the form says which it is.
     */
    catalogue: (options: { productCode?: string; includeInactive?: boolean } = {}) =>
      core<unknown>(`/platform/features${query(options)}`).then((payload) =>
        rowsWith<FeatureDefinitionSummary>(unwrap(payload, 'features'), ['code', 'name']),
      ),
    feature: (featureCode: string) =>
      core<unknown>(`/platform/features/${seg(featureCode)}`).then((payload) =>
        objectWith<FeatureDefinitionSummary>(payload, ['code', 'name'], 'feature'),
      ),
  },

  /** PA-09 and PA-10. Registrations and the mappings hung off them. */
  integrations: {
    list: (institutionId: string) =>
      core<unknown>(`/platform/integrations/institutions/${seg(institutionId)}`).then((payload) =>
        rowsWith<IntegrationRegistration>(unwrap(payload, 'integrations'), ['id', 'code', 'state']),
      ),
    get: (integrationId: string) =>
      core<unknown>(`/platform/integrations/${seg(integrationId)}`).then((payload) =>
        objectWith<IntegrationRegistration>(payload, ['id', 'code', 'state'], 'integration'),
      ),
    register: (institutionId: string, payload: RegisterIntegrationRequest) =>
      core<unknown>(`/platform/integrations/institutions/${seg(institutionId)}`, body(payload)).then((result) =>
        objectWith<IntegrationRegistration>(result, ['id', 'code', 'state'], 'integration'),
      ),
    update: (integrationId: string, payload: UpdateIntegrationRequest) =>
      core<unknown>(`/platform/integrations/${seg(integrationId)}`, patch(payload)).then((result) =>
        objectWith<IntegrationRegistration>(result, ['id', 'code', 'state'], 'integration'),
      ),
    setState: (integrationId: string, payload: SetIntegrationStateRequest) =>
      core<unknown>(`/platform/integrations/${seg(integrationId)}/state`, patch(payload)).then((result) =>
        objectWith<IntegrationRegistration>(result, ['id', 'code', 'state'], 'integration'),
      ),

    /**
     * Asks the server to probe the connector and report what it found.
     *
     * Deployed, and the response is `{ registration, outcome, stateRecorded }` —
     * three things, not one. The console unwrapped an `integration` envelope that
     * does not exist, so the parse threw and a working probe reported itself as
     * unavailable. The outcome comes back alongside the registration because the
     * two are not the same fact: a timeout and an unreachable host are both
     * non-healthy, and they send the operator to different places.
     */
    testConnectivity: (integrationId: string) =>
      core<unknown>(`/platform/integrations/${seg(integrationId)}/health-check`, body({})).then((payload) => {
        const root = isRecord(payload) ? payload : {};
        return {
          registration: objectWith<IntegrationRegistration>(root.registration, ['id', 'code', 'state']),
          outcome: objectWith<IntegrationHealthCheckResult['outcome']>(root.outcome, ['kind', 'detail']),
          stateRecorded: root.stateRecorded === true,
        } satisfies IntegrationHealthCheckResult;
      }),

    mappings: (integrationId: string, entityType?: MappedEntityType, includeInactive = false) =>
      core<unknown>(
        `/platform/integrations/${seg(integrationId)}/mappings${query({ entityType, includeInactive })}`,
      ).then((payload) =>
        rowsWith<IntegrationMapping>(unwrap(payload, 'mappings'), [
          'id',
          'entityType',
          'internalId',
          'externalId',
        ]),
      ),
    createMapping: (integrationId: string, payload: CreateMappingRequest) =>
      core<unknown>(`/platform/integrations/${seg(integrationId)}/mappings`, body(payload)).then((result) =>
        objectWith<IntegrationMapping>(result, ['id', 'entityType', 'internalId', 'externalId'], 'mapping'),
      ),
    deactivateMapping: (integrationId: string, mappingId: string, reason: string) =>
      core<unknown>(
        `/platform/integrations/${seg(integrationId)}/mappings/${seg(mappingId)}/deactivation`,
        patch({ reason }),
      ).then((result) =>
        objectWith<IntegrationMapping>(result, ['id', 'entityType', 'internalId', 'externalId'], 'mapping'),
      ),
    mappingHistory: (integrationId: string) =>
      core<unknown>(`/platform/integrations/${seg(integrationId)}/mappings-history`).then((payload) =>
        rowsWith<IntegrationMappingHistoryEntry>(unwrap(payload, 'history'), ['id']),
      ),
  },

  /**
   * The environment-level service connections, shared by every institution.
   *
   * Nothing here substitutes invented data for a failure. Every one of these calls
   * is allowed to fail and the screens behind them say which kind of failure it
   * was, because an operator who is told the console cannot see the Digital Switch
   * will go and look at the Digital Switch, and an operator shown an empty table
   * will not.
   */
  connections: {
    list: (
      options: {
        environment?: IntegrationEnvironment;
        systemCode?: IntegrationSystemCode;
        /** Answers "which connection serves NOTIFICATION_SMS here" in one query. */
        capability?: IntegrationCapability;
        status?: IntegrationState;
      } = {},
    ) =>
      core<unknown>(`${CONNECTIONS_PATH}${query(options)}`).then((payload) =>
        rowsWith<PlatformConnection>(unwrap(payload, 'connections'), [
          'id',
          'systemCode',
          'environment',
          'status',
        ]).map(normalizeConnection),
      ),

    /**
     * Every connection's health, including the ones nothing has ever contacted.
     *
     * A separate endpoint rather than something this client derives, because
     * `reporting` is the server's judgement and is not a function of `status`
     * alone: a connection with no address at all is UNKNOWN, one that has never
     * been probed is NOT_REPORTING, and neither of those is a fault. Deriving it
     * here would put a second, drifting copy of that rule in a browser.
     */
    health: (environment?: IntegrationEnvironment) =>
      core<unknown>(`${CONNECTIONS_PATH}/health${query({ environment })}`).then((payload) =>
        rowsWith<ConnectionHealthView>(unwrap(payload, 'connections', 'health'), [
          'id',
          'systemCode',
          'environment',
          'status',
          'reporting',
        ]).map((view) => ({ ...view, capabilities: capabilityList(view.capabilities) })),
      ),

    get: (connectionId: string) =>
      core<unknown>(`${CONNECTIONS_PATH}/${seg(connectionId)}`).then((payload) =>
        normalizeConnection(
          objectWith<PlatformConnection>(
            payload,
            ['id', 'systemCode', 'environment', 'status'],
            'connection',
          ),
        ),
      ),

    register: (payload: RegisterPlatformConnectionRequest) =>
      core<unknown>(CONNECTIONS_PATH, body(payload)).then((result) =>
        normalizeConnection(
          objectWith<PlatformConnection>(
            result,
            ['id', 'systemCode', 'environment', 'status'],
            'connection',
          ),
        ),
      ),

    /**
     * Amends the endpoint and its operational settings.
     *
     * Never the credential reference and never the identity pair — see
     * UpdatePlatformConnectionRequest for why both are excluded rather than merely
     * left unset by the form.
     */
    update: (connectionId: string, payload: UpdatePlatformConnectionRequest) =>
      core<unknown>(`${CONNECTIONS_PATH}/${seg(connectionId)}`, patch(payload)).then((result) =>
        normalizeConnection(
          objectWith<PlatformConnection>(
            result,
            ['id', 'systemCode', 'environment', 'status'],
            'connection',
          ),
        ),
      ),

    /**
     * Contacts the far end now and records what came back.
     *
     * Runs server-side and cannot run anywhere else: the credential is a vault
     * pointer that never leaves that process, so there is no version of this test a
     * browser could perform. A failure of this call is a failure of the PROBE, not
     * a verdict on the connection — "we could not check" and "it is broken" send an
     * operator to different places.
     */
    /**
     * Which credentials this connection holds — statuses only.
     *
     * There is no endpoint that returns a secret and this is not one: the response
     * carries `configured`, a twelve-character fingerprint, and who set it when. A lost
     * credential is rotated at the issuing console, not read back from here.
     */
    secrets: (connectionId: string) =>
      core<unknown>(`${CONNECTIONS_PATH}/${seg(connectionId)}/secrets`).then((payload) =>
        rowsWith<ConnectionSecretStatus>(unwrap(payload, 'secrets'), ['kind', 'configured']),
      ),

    /** Stores or rotates one credential. The value is write-only and never comes back. */
    putSecret: (connectionId: string, payload: PutConnectionSecretRequest) =>
      core<unknown>(`${CONNECTIONS_PATH}/${seg(connectionId)}/secrets`, {
        ...body(payload),
        method: 'PUT',
      }).then((result) => objectWith<ConnectionSecretStatus>(result, ['kind', 'configured'])),

    deleteSecret: (connectionId: string, kind: ConnectionSecretKind) =>
      core<unknown>(`${CONNECTIONS_PATH}/${seg(connectionId)}/secrets/${seg(kind)}`, {
        method: 'DELETE',
      }),

    /**
     * Uses the stored credentials against the far end.
     *
     * Distinct from the health check, which cannot answer this: that probe classifies 401
     * and 403 as reachable, so a connection with a wrong secret reads green. INSUFFICIENT_SCOPE
     * from this call is a PASS — it means the signature verified and only the permission
     * check declined.
     */
    credentialTest: (connectionId: string) =>
      core<unknown>(`${CONNECTIONS_PATH}/${seg(connectionId)}/credential-test`, body({})).then(
        (result) => objectWith<CredentialTestResult>(result, ['outcome', 'detail']),
      ),

    testConnection: (connectionId: string) =>
      core<unknown>(`${CONNECTIONS_PATH}/${seg(connectionId)}/health-check`, body({})).then((payload) => {
        const root = isRecord(payload) ? payload : {};
        return {
          connection: normalizeConnection(
            objectWith<PlatformConnection>(root.connection ?? payload, [
              'id',
              'systemCode',
              'environment',
              'status',
            ]),
          ),
          outcome: objectWith<IntegrationProbeResult>(root.outcome, ['kind', 'detail']),
          statusRecorded: root.statusRecorded === true,
        } satisfies PlatformConnectionProbeResult;
      }),

    /**
     * Switches a connection off. An administrative decision, never a probe's.
     *
     * The reason is part of this signature rather than of the dialog alone, so a
     * future caller cannot disable a connection every institution depends on and
     * leave no trace of why.
     */
    disable: (connectionId: string, reason: string) =>
      core<unknown>(`${CONNECTIONS_PATH}/${seg(connectionId)}/disable`, patch({ reason })).then((result) =>
        normalizeConnection(
          objectWith<PlatformConnection>(
            result,
            ['id', 'systemCode', 'environment', 'status'],
            'connection',
          ),
        ),
      ),

    /**
     * Points the connection at a different vault path.
     *
     * The parameter is a REFERENCE. There is no overload of this that takes a
     * secret, and there must not be: the server rejects one, and the presence of
     * the field is itself the hazard. The server clears `lastSuccessfulAt` as part
     * of this, because the proof that the connection worked was proof about the
     * old credential.
     */
    rotateCredentialReference: (connectionId: string, payload: RotateCredentialReferenceRequest) =>
      core<unknown>(`${CONNECTIONS_PATH}/${seg(connectionId)}/credential-reference`, patch(payload)).then(
        (result) =>
          normalizeConnection(
            objectWith<PlatformConnection>(
              result,
              ['id', 'systemCode', 'environment', 'status'],
              'connection',
            ),
          ),
      ),

    /**
     * Registration, amendments, rotations, probe results and disablement.
     *
     * Read from the platform governance trail rather than a table of its own, so
     * "who changed the production Switch credential, and when" is answered from the
     * same stream as every other administrative action.
     */
    audit: (connectionId: string) =>
      core<unknown>(`${CONNECTIONS_PATH}/${seg(connectionId)}/audit`).then((payload) =>
        rowsWith<PlatformAuditRecord>(unwrap(payload, 'records', 'audit'), [
          'id',
          'action',
          'occurredAt',
        ]),
      ),
  },

  /**
   * One institution's identity inside each connected system.
   *
   * Separate from `connections` because the two answer different questions and
   * fail independently. A screen that shows a mapping must never let a healthy
   * connection imply the institution is wired up — the mapping carries its own
   * status and its own verification, and NOT_CONFIGURED against a HEALTHY
   * connection is the most common state in this whole area.
   *
   * Every route here is platform-admin. The institution's own console reaches a
   * deliberately narrower shape at `institutions/:id/integration-status`, which
   * omits addresses and credential references entirely and permits a retry but
   * never an edit — a school administrator who mistypes a tenant id routes their
   * own fee postings into another institution's tenant.
   */
  institutionIntegrations: {
    list: (institutionId: string) =>
      core<unknown>(institutionMappingsPath(institutionId)).then((payload) =>
        rowsWith<InstitutionIntegrationMapping>(unwrap(payload, 'mappings'), [
          'id',
          'institutionId',
          'platformConnectionId',
          'status',
        ]).map(normalizeMapping),
      ),

    create: (institutionId: string, payload: CreateInstitutionMappingRequest) =>
      core<unknown>(institutionMappingsPath(institutionId), body(payload)).then((result) =>
        normalizeMapping(
          objectWith<InstitutionIntegrationMapping>(
            result,
            ['id', 'institutionId', 'platformConnectionId', 'status'],
            'mapping',
          ),
        ),
      ),

    update: (institutionId: string, mappingId: string, payload: UpdateInstitutionMappingRequest) =>
      core<unknown>(`${institutionMappingsPath(institutionId)}/${seg(mappingId)}`, patch(payload)).then(
        (result) =>
          normalizeMapping(
            objectWith<InstitutionIntegrationMapping>(
              result,
              ['id', 'institutionId', 'platformConnectionId', 'status'],
              'mapping',
            ),
          ),
      ),

    /**
     * Asks the far end whether THIS institution's identifiers actually resolve.
     *
     * Not the same call as a connection probe and not a substitute for one: the
     * probe answers "can CampusOne reach this host", and this answers "and does
     * that host know who we say this school is". The server refuses it while the
     * connection has never had a successful probe, because a verified mapping over
     * a connection nothing has ever answered is a green tick with nothing behind
     * it. The updated mapping comes back, and `verifiedAt` is the verdict.
     */
    verify: (institutionId: string, mappingId: string) =>
      core<unknown>(
        `${institutionMappingsPath(institutionId)}/${seg(mappingId)}/verification`,
        body({}),
      ).then((result) =>
        normalizeMapping(
          objectWith<InstitutionIntegrationMapping>(
            result,
            ['id', 'institutionId', 'platformConnectionId', 'status'],
            'mapping',
          ),
        ),
      ),

    /** Retires the mapping for this institution. The row is kept. */
    disable: (institutionId: string, mappingId: string, reason: string) =>
      core<unknown>(
        `${institutionMappingsPath(institutionId)}/${seg(mappingId)}/deactivation`,
        patch({ reason }),
      ).then((result) =>
        normalizeMapping(
          objectWith<InstitutionIntegrationMapping>(
            result,
            ['id', 'institutionId', 'platformConnectionId', 'status'],
            'mapping',
          ),
        ),
      ),

    /** Read from the governance trail, so it is the same stream as everything else. */
    history: (institutionId: string, mappingId: string) =>
      core<unknown>(`${institutionMappingsPath(institutionId)}/${seg(mappingId)}/history`).then(
        (payload) =>
          rowsWith<PlatformAuditRecord>(unwrap(payload, 'records', 'history'), [
            'id',
            'action',
            'occurredAt',
          ]),
      ),
  },

  /** PA-03. Campuses, branches and groups. Academic structure is EduCore's. */
  structure: {
    /**
     * Creates the institution an onboarding case exists to produce.
     *
     * THE STEP THAT WAS MISSING FROM THIS CONSOLE ENTIRELY. Core has served
     * `POST /platform/structure/institutions` all along — it creates the tenant, the
     * institution, the primary campus and the head-office branch in one transaction and
     * writes the new institution's id back onto the case. Nothing here ever called it,
     * so `institutionId` never arrived on a case, and an operator who worked a case to
     * ACTIVE ended up with a case marked live and no institution anywhere. The register
     * was then empty, correctly, with nothing on any screen to say why.
     *
     * Bootstrap BEFORE activating. Core now refuses to activate a case that has not
     * produced an institution, because ACTIVE leads only to SUSPENDED or CLOSED — a case
     * activated without one can never go back and bootstrap, and its institutionCode is
     * held under a unique constraint that blocks re-keying it as a new case.
     */
    bootstrapInstitution: (payload: BootstrapInstitutionRequest) =>
      core<unknown>('/platform/structure/institutions', body(payload)).then((result) =>
        objectWith<InstitutionStructure>(result, ['institution'], 'structure'),
      ),
    get: (institutionId: string) =>
      core<unknown>(`/platform/structure/institutions/${seg(institutionId)}`).then((payload) => {
        const root = isRecord(payload) ? payload : {};
        return {
          institution: isRecord(root.institution)
            ? (root.institution as unknown as PlatformInstitutionRecord)
            : undefined,
          campuses: rowsWith<Campus>(root.campuses, ['id', 'code', 'name']),
          branches: rowsWith<Branch>(root.branches, ['id', 'code', 'name']),
          groups: Array.isArray(root.groups)
            ? root.groups.filter((entry): entry is GroupMembershipSummary => isRecord(entry) && isRecord(entry.group))
            : [],
        } satisfies InstitutionStructure;
      }),
    addCampus: (institutionId: string, payload: CreateCampusRequest) =>
      core<unknown>(`/platform/structure/institutions/${seg(institutionId)}/campuses`, body(payload)).then(
        (result) => objectWith<Campus>(result, ['id', 'code', 'name'], 'campus'),
      ),
    addBranch: (institutionId: string, payload: CreateBranchRequest) =>
      core<unknown>(`/platform/structure/institutions/${seg(institutionId)}/branches`, body(payload)).then(
        (result) => objectWith<Branch>(result, ['id', 'code', 'name'], 'branch'),
      ),
    listGroups: () =>
      core<unknown>('/platform/structure/groups').then((payload) =>
        rowsWith<InstitutionGroup>(unwrap(payload, 'groups'), ['id', 'code', 'name']),
      ),
    createGroup: (payload: CreateGroupRequest) =>
      core<unknown>('/platform/structure/groups', body(payload)).then((result) =>
        objectWith<InstitutionGroup>(result, ['id', 'code', 'name'], 'group'),
      ),
    groupMembers: (groupId: string) =>
      core<unknown>(`/platform/structure/groups/${seg(groupId)}/members`).then((payload) =>
        rowsWith<InstitutionGroupMember>(unwrap(payload, 'members'), ['id', 'institutionId']),
      ),
    addGroupMember: (groupId: string, institutionId: string, role: GroupMembershipRole) =>
      core<unknown>(`/platform/structure/groups/${seg(groupId)}/members`, body({ institutionId, role })).then(
        (result) => objectWith<InstitutionGroupMember>(result, ['id', 'institutionId'], 'member'),
      ),
    /**
     * The group's live financial and product shape, generated by core on
     * request. See GroupAnalytics for the contract; the analytics phase may
     * not be deployed yet, and callers treat this like every other core
     * surface — a 404 is "not available yet", not a broken group.
     */
    groupAnalytics: (groupId: string) =>
      core<unknown>(`/platform/structure/groups/${seg(groupId)}/analytics`).then(normalizeGroupAnalytics),
    /**
     * POST /platform/structure/groups/:groupId/products — the group's own
     * product surface, distinct from every single-institution route.
     *
     * The body is `{ productCode, action, institutionIds? }`; omitted
     * institutionIds means every current member. The result is a per-member
     * report — applied, skipped (with reasons), and ids that are not members
     * at all — which the products tab renders rather than reducing to one
     * status line. `unknown productCode` is a 400 from core, so the picker is
     * fed from the catalogue and a server refusal is shown as the error it is.
     */
    bulkApplyGroupProduct: (groupId: string, payload: BulkApplyGroupProductRequest) =>
      core<unknown>(`/platform/structure/groups/${seg(groupId)}/products`, body(payload)).then(
        normalizeBulkProductResult,
      ),
    /**
     * Partial update — only the fields present in the body change. There is
     * deliberately no deactivate option here: no route toggles isActive, and a
     * settings tab that pretends it can end-date a group would be lying.
     */
    updateGroup: (groupId: string, payload: UpdateGroupRequest) =>
      core<unknown>(`/platform/structure/groups/${seg(groupId)}`, patch(payload)).then((result) =>
        objectWith<InstitutionGroup>(result, ['id', 'code', 'name'], 'group'),
      ),
    /**
     * Removes a member from the group. Core answers with a 204; there is no
     * row left to read back, so this resolves to the removed member's scope so
     * the caller can drop it from its list without refetching.
     */
    removeGroupMember: (groupId: string, institutionId: string) =>
      core<unknown>(`/platform/structure/groups/${seg(groupId)}/members/${seg(institutionId)}`, del()).then(
        (result) => (isRecord(result) ? objectWith<InstitutionGroupMember>(result, ['institutionId'], 'member') : undefined),
      ),
    /**
     * Moves a member from one role to another. Promoting a member to
     * HEAD_OFFICE demotes the incumbent — core enforces exactly one — and the
     * panel says so before it asks.
     */
    changeGroupMemberRole: (groupId: string, institutionId: string, role: GroupMembershipRole) =>
      core<unknown>(
        `/platform/structure/groups/${seg(groupId)}/members/${seg(institutionId)}`,
        patch({ role }),
      ).then((result) => objectWith<InstitutionGroupMember>(result, ['id', 'institutionId'], 'member')),
  },

  /**
   * PA-06. Core's branding controller, which is not platform-prefixed.
   *
   * `GET :id/settings`, not `GET :id`. The bare route returns RESOLVED branding
   * with platform defaults already applied — correct for a portal painting a
   * page, wrong for an editor, because every default would look like a value this
   * institution chose and saving the form would write the defaults in as
   * overrides. The editor route is the one core documents as being for exactly
   * this screen.
   *
   * The update is a PUT and carries no `reason`. The console sent a POST with one,
   * and since the DTO forbids unrecognised fields the request was a 400 before the
   * wrong verb was even reached: no branding change has ever saved from here.
   */
  branding: {
    /** The stored row, nulls intact, for the editor. */
    settings: (institutionId: string) =>
      core<unknown>(`/branding/${seg(institutionId)}/settings`).then((payload) =>
        objectWith<InstitutionBranding>(payload, ['institutionId'], 'branding'),
      ),

    /** Fully resolved branding, for anything that paints a page. */
    resolved: (institutionId: string) =>
      core<unknown>(`/branding/${seg(institutionId)}`).then((payload) =>
        isRecord(payload) ? (payload as unknown as InstitutionBranding) : { institutionId },
      ),

    update: (institutionId: string, payload: UpdateBrandingRequest) =>
      core<unknown>(`/branding/${seg(institutionId)}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }).then((result) => objectWith<InstitutionBranding>(result, ['institutionId'], 'branding')),

    /** Returns every appearance setting to the CSKY default. Destructive, so it asks why. */
    restoreDefaults: (institutionId: string, payload: RestoreBrandingDefaultsRequest) =>
      core<unknown>(`/branding/${seg(institutionId)}/restore-defaults`, body(payload)).then((result) =>
        objectWith<InstitutionBranding>(result, ['institutionId'], 'branding'),
      ),

    /**
     * Claims a custom domain and returns the TXT record that proves it.
     *
     * Its own endpoint, not a field on the branding patch — which is how the
     * console used to set it, and could never have worked. The domain is stored
     * PENDING_DNS and is not served until a check has passed.
     */
    claimDomain: (institutionId: string, domain: string) =>
      core<unknown>(`/branding/${seg(institutionId)}/custom-domain`, body({ domain })).then((result) =>
        objectWith<DomainVerificationInstruction>(result, ['domain', 'recordName', 'recordValue', 'state']),
      ),

    domainInstruction: (institutionId: string) =>
      core<unknown>(`/branding/${seg(institutionId)}/custom-domain/instruction`).then((payload) =>
        objectWith<DomainVerificationInstruction>(payload, ['domain', 'recordName', 'recordValue', 'state']),
      ),

    /** Looks for the record and records the outcome. Safe to call repeatedly. */
    verifyDomain: (institutionId: string) =>
      core<unknown>(`/branding/${seg(institutionId)}/custom-domain/verify`, body({})).then((result) =>
        objectWith<InstitutionBranding>(result, ['institutionId'], 'branding'),
      ),
  },

  /**
   * PA-11. Platform policy versions, the security floor, and per-institution
   * overrides.
   *
   * There is no `/platform/policies` list and no `PATCH /platform/policies/:id`;
   * the console called both and got 404s for a module that is fully built.
   * Policies are not rows with ids — they are a keyed document published as a
   * version, plus overrides an institution holds against individual keys.
   */
  policies: {
    /** The CSKY security floor, with the reason for each bound. */
    minimums: () =>
      core<unknown>('/platform/policies/minimums').then((payload) =>
        isRecord(payload) ? (payload as PolicyMinimums) : {},
      ),

    /** Every platform policy version, newest first. */
    versions: () =>
      core<unknown>('/platform/policies/versions').then((payload) =>
        rowsWith<PlatformPolicyVersion>(unwrap(payload, 'versions'), [
          'id',
          'versionNumber',
          'status',
          'effectiveFrom',
        ]),
      ),

    /**
     * The version in force now, or on a given date.
     *
     * Null is a real answer: nothing is published and the in-code baseline is
     * answering. The screen says so rather than showing a blank version number.
     */
    versionInForce: (asOf?: string) =>
      core<unknown>(`/platform/policies/versions/in-force${query({ asOf })}`).then((payload) =>
        isRecord(payload) && typeof payload.versionNumber === 'number'
          ? (payload as unknown as PlatformPolicyVersion)
          : null,
      ),

    publishVersion: (payload: {
      document: Record<string, unknown>;
      effectiveFrom: string;
      changeReason: string;
    }) =>
      core<unknown>('/platform/policies/versions', body(payload)).then((result) =>
        objectWith<PlatformPolicyVersion>(result, ['id', 'versionNumber', 'status'], 'version'),
      ),

    /** What actually applies to an institution, and where each value came from. */
    resolved: (institutionId: string, asOf?: string) =>
      core<unknown>(`/platform/policies/institutions/${seg(institutionId)}${query({ asOf })}`).then(
        normalizeResolvedPolicies,
      ),

    /** Every override the institution has held, including revoked ones. */
    overrides: (institutionId: string) =>
      core<unknown>(`/platform/policies/institutions/${seg(institutionId)}/overrides`).then((payload) =>
        rowsWith<InstitutionPolicyOverride>(
          unwrap(payload, 'overrides'),
          ['id', 'policyKey', 'reason', 'effectiveFrom'],
          ['value'],
        ),
      ),

    grantOverride: (institutionId: string, payload: GrantPolicyOverrideRequest) =>
      core<unknown>(`/platform/policies/institutions/${seg(institutionId)}/overrides`, body(payload)).then(
        (result) => objectWith<InstitutionPolicyOverride>(result, ['id', 'policyKey'], 'override'),
      ),

    revokeOverride: (institutionId: string, overrideId: string, reason: string) =>
      core<unknown>(
        `/platform/policies/institutions/${seg(institutionId)}/overrides/${seg(overrideId)}`,
        del({ reason }),
      ).then((result) => objectWith<InstitutionPolicyOverride>(result, ['id', 'policyKey'], 'override')),
  },

  /**
   * PA-15. CSKY-owned reference data.
   *
   * Sixteen domains, not eight "sets", and there is no upsert: a change publishes a
   * NEW VERSION and end-dates the old one. The console's editor posted a `labelFr`
   * and an `active` boolean, neither of which exists — and because the DTO forbids
   * unrecognised fields, the whole request was a 400 rather than a partial save.
   */
  masterData: {
    domains: () =>
      core<unknown>('/platform/master-data/domains').then((payload) => {
        const root = isRecord(payload) ? payload : {};
        return stringList(root.domains) as MasterDataDomain[];
      }),

    list: (domain: MasterDataDomain, includeRetired = false) =>
      core<unknown>(`/platform/master-data/${seg(domain)}${query({ includeRetired })}`).then((payload) =>
        rowsWith<MasterDataValue>(unwrap(payload, 'values'), ['id', 'code', 'label', 'status', 'version']),
      ),

    /** Every version of a code, newest first. Nothing is ever removed from it. */
    history: (domain: MasterDataDomain, code: string) =>
      core<unknown>(`/platform/master-data/${seg(domain)}/${seg(code)}/history`).then((payload) =>
        rowsWith<MasterDataValue>(unwrap(payload, 'versions'), ['id', 'code', 'label', 'status', 'version']),
      ),

    /** The version in force at a point in time. */
    resolve: (domain: MasterDataDomain, code: string, at?: string) =>
      core<unknown>(`/platform/master-data/${seg(domain)}/${seg(code)}/resolve${query({ at })}`).then(
        (payload) => objectWith<MasterDataValue>(payload, ['id', 'code', 'label', 'status', 'version'], 'value'),
      ),

    /**
     * Where a value is used, and what could not be checked.
     *
     * `blindSpots` is rendered next to the observed usage, never below a fold: an
     * empty observed list does NOT mean the value is unused, it means nothing in
     * core cites it, and the services named in blindSpots cannot be queried from
     * here. Presenting the first as the second is how a code still in use in
     * EduCore gets retired.
     */
    usage: (domain: MasterDataDomain, code: string) =>
      core<unknown>(`/platform/master-data/${seg(domain)}/${seg(code)}/usage`).then((payload) => {
        const root = isRecord(payload) ? payload : {};
        return {
          observed: rowsWith<{ source: string; count: number }>(root.observed, ['source', 'count']),
          blindSpots: stringList(root.blindSpots),
        };
      }),

    /** What would stop this value being withdrawn, without withdrawing it. */
    retirementPreview: (domain: MasterDataDomain, code: string) =>
      core<unknown>(`/platform/master-data/${seg(domain)}/${seg(code)}/retirement-preview`).then((payload) => {
        const root = isRecord(payload) ? payload : {};
        return {
          canRetire: root.canRetire === true,
          blockingReferences: rowsWith<{ domain: string; code: string }>(root.blockingReferences, [
            'domain',
            'code',
          ]),
          blockingUsage: stringList(root.blockingUsage),
          unverifiableIn: stringList(root.unverifiableIn),
        };
      }),

    create: (domain: MasterDataDomain, payload: CreateMasterDataValueRequest) =>
      core<unknown>(`/platform/master-data/${seg(domain)}`, body(payload)).then((result) =>
        objectWith<MasterDataValue>(result, ['id', 'code', 'label', 'status', 'version'], 'value'),
      ),

    publishVersion: (domain: MasterDataDomain, code: string, payload: PublishMasterDataVersionRequest) =>
      core<unknown>(`/platform/master-data/${seg(domain)}/${seg(code)}/versions`, body(payload)).then((result) =>
        objectWith<MasterDataValue>(result, ['id', 'code', 'label', 'status', 'version'], 'value'),
      ),

    changeStatus: (domain: MasterDataDomain, code: string, payload: ChangeMasterDataStatusRequest) =>
      core<unknown>(`/platform/master-data/${seg(domain)}/${seg(code)}/status`, body(payload)).then((result) =>
        objectWith<MasterDataValue>(result, ['id', 'code', 'label', 'status', 'version'], 'value'),
      ),

    /** Withdraws a value — end-dated, never deleted. */
    retire: (
      domain: MasterDataDomain,
      code: string,
      payload: { reason: string; acknowledgeUnverifiableUsage?: boolean },
    ) =>
      core<unknown>(`/platform/master-data/${seg(domain)}/${seg(code)}/retire`, body(payload)).then((result) =>
        objectWith<MasterDataValue>(result, ['id', 'code', 'label', 'status', 'version'], 'value'),
      ),
  },

  /**
   * PA-13. Operations, in the three pieces core actually serves.
   *
   * `/platform/operations` on its own is not a route. Health, incidents and
   * maintenance are separate resources with separate write paths, and collapsing
   * them into one snapshot request is what produced the permanent 404 on this
   * screen.
   */
  operations: {
    health: () => core<unknown>('/platform/operations/health').then(normalizeHealth),

    incidents: (options: { openOnly?: boolean; institutionId?: string } = {}) =>
      core<unknown>(`/platform/operations/incidents${query(options)}`).then((payload) =>
        rowsWith<PlatformIncident>(unwrap(payload, 'incidents'), [
          'id',
          'reference',
          'title',
          'severity',
          'status',
          'detectedAt',
        ]),
      ),

    raiseIncident: (payload: {
      title: string;
      summary: string;
      severity: IncidentSeverity;
      platformWide: boolean;
      affectedInstitutionIds?: string[];
      affectedProductCodes?: string[];
      detectedAt?: string;
    }) =>
      core<unknown>('/platform/operations/incidents', body(payload)).then((result) =>
        objectWith<PlatformIncident>(result, ['id', 'reference', 'status'], 'incident'),
      ),

    updateIncident: (
      incidentId: string,
      payload: {
        status?: Exclude<IncidentStatus, 'RESOLVED'>;
        severity?: IncidentSeverity;
        summary?: string;
        affectedInstitutionIds?: string[];
        affectedProductCodes?: string[];
        platformWide?: boolean;
      },
    ) =>
      core<unknown>(`/platform/operations/incidents/${seg(incidentId)}`, patch(payload)).then((result) =>
        objectWith<PlatformIncident>(result, ['id', 'reference', 'status'], 'incident'),
      ),

    /**
     * Resolves an incident. Its own route, because it demands a note.
     *
     * Resolving is not a status change like the others: mitigated means the symptom
     * is gone, resolved means it will not come back for the same reason, and only
     * one of those needs somebody to write down what was done.
     */
    resolveIncident: (incidentId: string, resolution: string) =>
      core<unknown>(`/platform/operations/incidents/${seg(incidentId)}/resolution`, body({ resolution })).then(
        (result) => objectWith<PlatformIncident>(result, ['id', 'reference', 'status'], 'incident'),
      ),

    maintenance: (institutionId?: string) =>
      core<unknown>(`/platform/operations/maintenance${query({ institutionId })}`).then((payload) =>
        rowsWith<ProductMaintenanceWindow>(unwrap(payload, 'windows'), [
          'id',
          'productCode',
          'mode',
          'startsAt',
          'endsAt',
        ]),
      ),

    scheduleMaintenance: (payload: {
      productCode: string;
      institutionId?: string;
      mode: MaintenanceMode;
      startsAt: string;
      endsAt: string;
      userMessage: string;
    }) =>
      core<unknown>('/platform/operations/maintenance', body(payload)).then((result) =>
        objectWith<ProductMaintenanceWindow>(result, ['id', 'productCode', 'mode'], 'window'),
      ),

    /** Ends a window early. The plan is kept, not edited. */
    cancelMaintenance: (windowId: string) =>
      core<unknown>(`/platform/operations/maintenance/${seg(windowId)}`, del()).then((result) =>
        objectWith<ProductMaintenanceWindow>(result, ['id', 'productCode', 'mode'], 'window'),
      ),
  },

  /**
   * PA-16. Imports, which core calls BATCHES.
   *
   * `/platform/imports` is not a route; `/platform/imports/batches` is, and
   * everything about one batch hangs off it. The name is not cosmetic — a batch is
   * a thing with a state, an owner and an approval, and nothing in it has taken
   * effect until `approvedAt` is set. The console's flat "import job" with accepted
   * and rejected row counts described a fire-and-forget upload this service
   * deliberately does not offer.
   */
  imports: {
    templates: () =>
      core<unknown>('/platform/imports/templates').then((payload) =>
        rowsWith<{ code: string; title: string }>(unwrap(payload, 'templates'), ['code']),
      ),

    list: (filters: { institutionId?: string; state?: ImportBatchState; templateCode?: string } = {}) =>
      core<unknown>(`/platform/imports/batches${query(filters)}`).then((payload) =>
        rowsWith<ImportBatch>(unwrap(payload, 'batches'), [
          'id',
          'batchReference',
          'templateCode',
          'state',
          'fileName',
        ]),
      ),

    get: (batchId: string) =>
      core<unknown>(`/platform/imports/batches/${seg(batchId)}`).then((payload) =>
        objectWith<ImportBatch>(payload, ['id', 'batchReference', 'templateCode', 'state'], 'batch'),
      ),

    /**
     * Counts of total, validated, quarantined and imported rows.
     *
     * Its own request because it is computed from the staged rows rather than from
     * counters on the batch, so it cannot disagree with them. Readable at any
     * point, not only at the end.
     */
    progress: (batchId: string) =>
      core<unknown>(`/platform/imports/batches/${seg(batchId)}/progress`).then((payload) =>
        objectWith<ImportProgress>(payload, ['batchId', 'state', 'totalRows'], 'progress'),
      ),

    /**
     * The rejected rows, with their reasons and the values that produced them.
     *
     * This is the screen. A completed import is not interesting; an import that
     * took four thousand rows and quarantined nine is, and the nine are invisible
     * from a status column.
     */
    quarantineReport: (batchId: string) =>
      core<unknown>(`/platform/imports/batches/${seg(batchId)}/quarantine/report`).then((payload) => {
        const root = isRecord(payload) ? payload : {};
        return {
          batchReference: optionalString(root.batchReference) ?? '',
          templateCode: optionalString(root.templateCode) ?? '',
          generatedAt: optionalString(root.generatedAt) ?? '',
          columns: stringList(root.columns),
          entries: rowsWith<QuarantineReportEntry>(
            root.entries,
            ['rowNumber', 'reasonCode', 'detail', 'resolution'],
            ['column'],
          ),
        } satisfies QuarantineReport;
      }),

    /** Where the CSV of the same report lives, for correcting and re-uploading. */
    quarantineReportCsvPath: (batchId: string) =>
      `/platform/imports/batches/${seg(batchId)}/quarantine/report.csv`,

    /** Drops quarantined rows from the import, with a reason and an actor. */
    waive: (batchId: string, rowNumbers: number[], reason: string) =>
      core<unknown>(
        `/platform/imports/batches/${seg(batchId)}/quarantine/waivers`,
        body({ rowNumbers, reason }),
      ).then((result) => objectWith<ImportBatch>(result, ['id', 'state'], 'batch')),

    /**
     * Approves the batch — the only step that lets anything take effect.
     *
     * `acknowledgeWaivedRows` is not a formality. Core refuses an approval that
     * silently carries waived rows, because a waiver is somebody deciding to drop
     * records and the approver has to have seen that decision.
     */
    approve: (batchId: string, payload: { note?: string; acknowledgeWaivedRows?: boolean }) =>
      core<unknown>(`/platform/imports/batches/${seg(batchId)}/approval`, body(payload)).then((result) =>
        objectWith<ImportBatch>(result, ['id', 'state'], 'batch'),
      ),

    /** Rejects, cancels or records the failure of a batch. */
    close: (batchId: string, payload: { state: ImportBatchState; reason: string }) =>
      core<unknown>(`/platform/imports/batches/${seg(batchId)}/closure`, body(payload)).then((result) =>
        objectWith<ImportBatch>(result, ['id', 'state'], 'batch'),
      ),
  },

  /**
   * PA-18. Twelve reports, twelve GET routes, and no run history.
   *
   * There is no `GET /platform/reports` index and no `/platform/reports/runs`; the
   * console asked for both and got two 404s. The index is now built from
   * PLATFORM_REPORT_ROUTES — see the note there for why that belongs on the client
   * — and there is no run history because every route on that controller is a GET
   * computed from live tables. A report that recorded that it ran would be a
   * reporting endpoint that changes state, which is one an operator triggers twice
   * by refreshing a dashboard.
   */
  reports: {
    /** The reports this console can open, with the route each one uses. */
    catalogue: () => PLATFORM_REPORT_ROUTES,

    /**
     * Runs one report now and returns its envelope.
     *
     * `metadata` is rendered in full and is not decoration: it names the tables the
     * numbers came from, what was and was not counted, and — in a field a machine
     * can branch on as well as a sentence a person can read — that these are
     * OPERATIONAL figures and not EduCore's academic record or anyone's financials.
     * A consumer that reconciles money against them will find a discrepancy that is
     * real and unexplainable from this side. A body with no metadata is refused
     * rather than rendered, because a table of numbers with nothing saying what
     * they count is the failure this whole envelope exists to prevent.
     */
    run: (code: PlatformReportCode, parameters: ReportParameterValues = {}) => {
      const route = PLATFORM_REPORT_ROUTES.find((candidate) => candidate.code === code);
      if (!route) {
        return Promise.reject(new Error(`No platform report is defined with code ${code}.`));
      }
      const accepted: Record<string, QueryValue> = {};
      for (const name of route.parameters) {
        accepted[name] = parameters[name];
      }
      return core<unknown>(`/platform/reports/${route.path}${query(accepted)}`).then((payload) => {
        const root = isRecord(payload) ? payload : {};
        if (!isRecord(root.metadata)) {
          throw new Error(
            'The server returned a report with no metadata, so nothing can say what these numbers count.',
          );
        }
        return {
          metadata: root.metadata as unknown as PlatformReport['metadata'],
          rows: Array.isArray(root.rows) ? root.rows.filter((row): row is ReportRow => isRecord(row)) : [],
        } satisfies PlatformReport;
      });
    },
  },
};
